import { ConversationBlock, ChatMessage } from "@/app/typing/chat";
import { artifact, artifact_list_item } from "@/app/typing/artifacts";
import { tag } from "@/app/typing/tags";
import { calendar_event } from "@/app/typing/events";
import { clearToolFoldoutMocks, setToolFoldoutMock } from "@/app/workspace/chat/chatWindow/toolFoldoutMocks";
import { chatState } from "@/app/chatState";
import { runInAction } from "mobx";

export type StreamPhase = {
    kind: "text" | "tool_use";
    tool?: { name: string; id: string };
    chunks: string[];
    /** After this tool phase commits, append tool_result + register foldout mock. */
    result?: ToolResultFixture;
    /** Text the backend saves for this phase when it differs from the streamed chunks, e.g. with citations baked in. */
    finalText?: string;
};

export type ToolResultFixture = {
    resultMessageId: number;
    foldout: any;
    /** Extra fields on the committed tool_result item, e.g. validate_citation's citations_batch. */
    resultFields?: Record<string, unknown>;
    /** Present for cruq object foldouts. */
    entity?: "artifact" | "tag" | "event";
    operation?: "create" | "update" | "query" | "read";
    /** Base objects that must exist before update foldouts apply revisions. */
    seedObjects?: Array<{ type: "artifact" | "tag" | "event"; id: number; label: string; created_at: string; data: any }>;
};

/** @deprecated Use ToolResultFixture */
export type CruqResultFixture = ToolResultFixture;

export type ChatMessageTestScenario = {
    id: string;
    label: string;
    seed: ConversationBlock[];
    phases?: StreamPhase[];
    /** Static completed tools already present in seed — register mocks + seed objects on load. */
    staticResults?: ToolResultFixture[];
    /** Objects loaded into chatState on load, e.g. the artifacts a reply cites. */
    objects?: ToolResultFixture["seedObjects"];
};

const CONV_ID = -9001;
const AGENT_ID = 1;
const NOW = "2026-08-09T10:00:00Z";

/** Split a string into small chunks so Step/Play can show partial UI states. */
export function chunkString(s: string, size = 8): string[] {
    const out: string[] = [];
    for (let i = 0; i < s.length; i += size) out.push(s.slice(i, i + size));
    return out;
}

type CruqEntity = "artifact" | "tag" | "event";
type CruqOp = "create" | "update" | "query" | "read";

function cruqToolJson(intent: string, type: CruqEntity, operation: CruqOp, input: Record<string, unknown>): string {
    return JSON.stringify({
        intent,
        type,
        operation,
        input: JSON.stringify(input),
    });
}

function userPrompt(id: number, text: string): ConversationBlock {
    return {
        role: "user",
        content: [{ id, role: "user", conversation_id: CONV_ID, content: text }],
    };
}

function toolUseMessage(
    id: number,
    toolId: string,
    input: { intent: string; type: CruqEntity; operation: CruqOp; input: Record<string, unknown> },
): ChatMessage {
    return {
        id,
        role: "assistant",
        agent_id: AGENT_ID,
        conversation_id: CONV_ID,
        content: [
            {
                type: "tool_use",
                id: toolId,
                name: "cruq_tool",
                input: {
                    intent: input.intent,
                    type: input.type,
                    operation: input.operation,
                    input: JSON.stringify(input.input),
                },
            },
        ],
    };
}

function toolResultMessage(id: number, toolId: string, toolName = "cruq_tool", fields?: Record<string, unknown>): ChatMessage {
    return {
        id,
        role: "user",
        conversation_id: CONV_ID,
        content: [{ type: "tool_result", id: toolId, name: toolName, ...fields }],
    };
}

function hashId(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return 1000 + Math.abs(h % 900000);
}

// ── Domain fixtures ────────────────────────────────────────────────────────

const ARTIFACT_CREATE_INPUT = {
    variable_name: "meeting_notes",
    content: "# Meeting notes\n\n- Budget approved\n- Ship Friday",
    tag_ids: [11, 12],
    event_id: 42,
};

const ARTIFACT_UPDATE_INPUT = {
    id: 101,
    edits: [
        {
            old_text: "- Budget approved",
            new_text: "- Budget approved\n- Added action items",
        },
    ],
};

const TAG_CREATE_INPUT = {
    name: "Product",
    description: "Product planning and roadmap tags.",
    scope: "organisation" as const,
    permission_type: 7,
    tpr_organisation_ids: [2],
    attributes: {},
};

const TAG_UPDATE_INPUT = {
    id: 201,
    scope: "organisation" as const,
    permission_type: 7,
    tpr_organisation_ids: [2],
    edits: [
        {
            old_text: "Product planning and roadmap tags.",
            new_text: "Product planning, roadmap, and launch tags.",
        },
    ],
    attributes: {},
};

const EVENT_CREATE_INPUT = {
    tag_id: 11,
    title: "Weekly sync",
    start_time: "2026-08-10T09:00:00Z",
    end_time: "2026-08-10T09:30:00Z",
    description: "Standup and blockers.",
    location: "Room A",
    all_day: false,
    timezone: "UTC",
};

const EVENT_UPDATE_INPUT = {
    event_id: 301,
    title: "Weekly sync (moved)",
    start_time: "2026-08-10T10:00:00Z",
    end_time: "2026-08-10T10:30:00Z",
    description: "Standup and blockers.",
    location: "Room B",
    all_day: false,
    timezone: "UTC",
};

const mockArtifact = (overrides: Partial<artifact> & Pick<artifact, "id" | "variable_name" | "content">): artifact => ({
    created_at: NOW,
    last_updated_at: NOW,
    tag_ids: overrides.tag_ids ?? [11],
    permission_type: 7,
    is_draft: true,
    user_lock: 0,
    event_id: overrides.event_id ?? null,
    ...overrides,
});

const mockTag = (overrides: Partial<tag> & Pick<tag, "id" | "name" | "description">): tag => ({
    created_at: NOW,
    last_updated_at: NOW,
    scope: "organisation",
    effective_permission_type: 7,
    is_draft: true,
    user_lock: 0,
    has_extension: false,
    has_calendar: false,
    attributes: {},
    permission_type: 7,
    tpr_organisation_ids: [2],
    organisation_id: 1,
    ...overrides,
});

const mockEvent = (
    overrides: Partial<calendar_event> & Pick<calendar_event, "id" | "title">,
): calendar_event => ({
    calendar_id: 1,
    description: null,
    location: null,
    start_time: "2026-08-10T09:00:00Z",
    end_time: "2026-08-10T09:30:00Z",
    all_day: false,
    timezone: "UTC",
    recurrence_rule: null,
    created_at: NOW,
    last_updated_at: NOW,
    is_draft: false,
    user_lock: null,
    tag_id: 11,
    tag_name: "Product",
    artifact_ids: [],
    ...overrides,
});

function artifactCreateResult(toolId: string, artId: number, input = ARTIFACT_CREATE_INPUT): CruqResultFixture {
    const art = mockArtifact({
        id: artId,
        variable_name: input.variable_name,
        content: input.content,
        tag_ids: input.tag_ids,
        event_id: input.event_id ?? null,
        is_draft: true,
    });
    return {
        resultMessageId: artId + 500000,
        entity: "artifact",
        operation: "create",
        foldout: {
            type: "tool_result",
            name: "cruq_tool",
            tool_id: toolId,
            entity_type: "artifact",
            operation: "create",
            content: { artifact: art },
        },
    };
}

function artifactQueryResult(toolId: string, items: artifact_list_item[]): CruqResultFixture {
    return {
        resultMessageId: hashId(`result-${toolId}`),
        entity: "artifact",
        operation: "query",
        foldout: {
            type: "tool_result",
            name: "cruq_tool",
            tool_id: toolId,
            entity_type: "artifact",
            operation: "query",
            content: items,
        },
    };
}

const ARTIFACT_QUERY_INPUT = {
    where: "t.id IN (11, 12)",
    limit: 5,
    order_by: "object.last_updated_at DESC",
};

const ARTIFACT_QUERY_FIVE: artifact_list_item[] = [
    {
        id: 501,
        variable_name: "meeting_notes",
        created_at: "2026-08-01T09:00:00Z",
        last_updated_at: "2026-08-08T12:00:00Z",
        tag_ids: [11],
        permission_type: 7,
        is_draft: false,
        user_lock: 0,
        event_id: 42,
    },
    {
        id: 502,
        variable_name: "roadmap_q3",
        created_at: "2026-07-15T10:00:00Z",
        last_updated_at: "2026-08-07T16:30:00Z",
        tag_ids: [11, 12],
        permission_type: 7,
        is_draft: false,
        user_lock: 0,
        event_id: null,
    },
    {
        id: 503,
        variable_name: "customer_feedback",
        created_at: "2026-07-20T11:00:00Z",
        last_updated_at: "2026-08-06T09:15:00Z",
        tag_ids: [12],
        permission_type: 3,
        is_draft: true,
        user_lock: 0,
        event_id: null,
    },
    {
        id: 504,
        variable_name: "sprint_retro",
        created_at: "2026-08-02T14:00:00Z",
        last_updated_at: "2026-08-05T18:00:00Z",
        tag_ids: [11],
        permission_type: 7,
        is_draft: false,
        user_lock: 0,
        event_id: 43,
    },
    {
        id: 505,
        variable_name: "hiring_plan",
        created_at: "2026-06-01T08:00:00Z",
        last_updated_at: "2026-08-04T11:45:00Z",
        tag_ids: [11, 12],
        permission_type: 7,
        is_draft: false,
        user_lock: 0,
        event_id: null,
    },
];

function artifactUpdateResult(toolId: string, artId: number, revId: number): CruqResultFixture {
    const base = mockArtifact({
        id: artId,
        variable_name: "meeting_notes",
        content: "# Meeting notes\n\n- Budget approved\n- Ship Friday",
        tag_ids: [11, 12],
        event_id: 42,
        is_draft: false,
    });
    const revision = {
        id: revId,
        artifact_id: artId,
        conversation_id: CONV_ID,
        content_diff: "--- a\n+++ b\n@@ -1,3 +1,4 @@\n # Meeting notes\n \n - Budget approved\n+- Added action items\n - Ship Friday\n",
        tag_ids: [11, 12],
        variable_name: "meeting_notes",
        created_at: NOW,
        last_updated_at: NOW,
        event_id: 42,
    };
    return {
        resultMessageId: revId + 500000,
        entity: "artifact",
        operation: "update",
        seedObjects: [
            {
                type: "artifact",
                id: artId,
                label: base.variable_name,
                created_at: base.created_at,
                data: base,
            },
        ],
        foldout: {
            type: "tool_result",
            name: "cruq_tool",
            tool_id: toolId,
            entity_type: "artifact",
            operation: "update",
            content: revision,
        },
    };
}

function tagCreateResult(toolId: string, tagId: number, input = TAG_CREATE_INPUT): CruqResultFixture {
    const t = mockTag({
        id: tagId,
        name: input.name,
        description: input.description,
        scope: input.scope,
        permission_type: input.permission_type,
        tpr_organisation_ids: input.tpr_organisation_ids,
        is_draft: true,
    });
    return {
        resultMessageId: tagId + 500000,
        entity: "tag",
        operation: "create",
        foldout: {
            type: "tool_result",
            name: "cruq_tool",
            tool_id: toolId,
            entity_type: "tag",
            operation: "create",
            content: { tag: t },
        },
    };
}

function tagUpdateResult(toolId: string, tagId: number, revId: number): CruqResultFixture {
    const base = mockTag({
        id: tagId,
        name: "Product",
        description: "Product planning and roadmap tags.",
        is_draft: false,
    });
    const revision = {
        id: revId,
        tag_id: tagId,
        conversation_id: CONV_ID,
        description_diff: "--- a\n+++ b\n@@ -1 +1 @@\n-Product planning and roadmap tags.\n+Product planning, roadmap, and launch tags.\n",
        scope: "organisation" as const,
        permission_type: 7,
        effective_permission: 7,
        tpr_organisation_ids: [2],
        name: "Product",
        created_at: NOW,
        last_updated_at: NOW,
        attributes: {},
    };
    return {
        resultMessageId: revId + 500000,
        entity: "tag",
        operation: "update",
        seedObjects: [
            {
                type: "tag",
                id: tagId,
                label: base.name,
                created_at: base.created_at,
                data: base,
            },
        ],
        foldout: {
            type: "tool_result",
            name: "cruq_tool",
            tool_id: toolId,
            entity_type: "tag",
            operation: "update",
            content: revision,
        },
    };
}

function eventCreateResult(toolId: string, eventId: number, input = EVENT_CREATE_INPUT): CruqResultFixture {
    const ev = mockEvent({
        id: eventId,
        title: input.title,
        start_time: input.start_time,
        end_time: input.end_time,
        description: input.description,
        location: input.location,
        all_day: input.all_day,
        timezone: input.timezone,
        tag_id: input.tag_id,
    });
    return {
        resultMessageId: eventId + 500000,
        entity: "event",
        operation: "create",
        foldout: {
            type: "tool_result",
            name: "cruq_tool",
            tool_id: toolId,
            entity_type: "event",
            operation: "create",
            content: { event: ev },
        },
    };
}

function eventUpdateResult(toolId: string, eventId: number, revId: number): CruqResultFixture {
    const base = mockEvent({
        id: eventId,
        title: "Weekly sync",
        location: "Room A",
    });
    const revision = {
        id: revId,
        event_id: eventId,
        conversation_id: CONV_ID,
        created_at: NOW,
        last_updated_at: NOW,
        title: "Weekly sync (moved)",
        start_time: "2026-08-10T10:00:00Z",
        end_time: "2026-08-10T10:30:00Z",
        description: "Standup and blockers.",
        location: "Room B",
        all_day: false,
        timezone: "UTC",
    };
    return {
        resultMessageId: revId + 500000,
        entity: "event",
        operation: "update",
        seedObjects: [
            {
                type: "event",
                id: eventId,
                label: base.title,
                created_at: base.start_time,
                data: base,
            },
        ],
        foldout: {
            type: "tool_result",
            name: "cruq_tool",
            tool_id: toolId,
            entity_type: "event",
            operation: "update",
            content: revision,
        },
    };
}

/** Register mocks + seed chatObjects for update bases / static completed tools. */
export function applyCruqResultFixtures(results: CruqResultFixture[], toolIds: string[]) {
    clearToolFoldoutMocks();
    results.forEach((r, i) => {
        const toolId = toolIds[i];
        if (!toolId) return;
        setToolFoldoutMock(toolId, r.foldout);
        seedChatObjects(r.seedObjects ?? []);
    });
}

function seedChatObjects(objects: NonNullable<ToolResultFixture["seedObjects"]>) {
    for (const obj of objects) {
        if (obj.type === "artifact") {
            chatState.chatObjects.update([
                {
                    key: { type: "artifact", id: obj.id },
                    value: {
                        type: "artifact",
                        id: obj.id,
                        label: obj.label,
                        created_at: obj.created_at,
                        artifact: obj.data,
                        unsaved_changes: false,
                    },
                },
            ]);
        } else if (obj.type === "tag") {
            chatState.chatObjects.update([
                {
                    key: { type: "tag", id: obj.id },
                    value: {
                        type: "tag",
                        id: obj.id,
                        label: obj.label,
                        created_at: obj.created_at,
                        tag: obj.data,
                        unsaved_changes: false,
                    },
                },
            ]);
        } else {
            chatState.chatObjects.update([
                {
                    key: { type: "event", id: obj.id },
                    value: {
                        type: "event",
                        id: obj.id,
                        label: obj.label,
                        created_at: obj.created_at,
                        event: obj.data,
                        unsaved_changes: false,
                    },
                },
            ]);
        }
    }
}

/** Append a tool_result message and register its foldout mock (after a streamed tool commits). */
export function commitToolResult(toolId: string, result: ToolResultFixture) {
    setToolFoldoutMock(toolId, result.foldout);

    for (const obj of result.seedObjects ?? []) {
        if (obj.type === "artifact") {
            chatState.chatObjects.update([
                {
                    key: { type: "artifact", id: obj.id },
                    value: {
                        type: "artifact",
                        id: obj.id,
                        label: obj.label,
                        created_at: obj.created_at,
                        artifact: obj.data,
                        unsaved_changes: false,
                    },
                },
            ]);
        } else if (obj.type === "tag") {
            chatState.chatObjects.update([
                {
                    key: { type: "tag", id: obj.id },
                    value: {
                        type: "tag",
                        id: obj.id,
                        label: obj.label,
                        created_at: obj.created_at,
                        tag: obj.data,
                        unsaved_changes: false,
                    },
                },
            ]);
        } else {
            chatState.chatObjects.update([
                {
                    key: { type: "event", id: obj.id },
                    value: {
                        type: "event",
                        id: obj.id,
                        label: obj.label,
                        created_at: obj.created_at,
                        event: obj.data,
                        unsaved_changes: false,
                    },
                },
            ]);
        }
    }

    const toolName = (result.foldout?.name as string) || "cruq_tool";
    runInAction(() => {
        const msg = toolResultMessage(result.resultMessageId, toolId, toolName, result.resultFields);
        // Like chatState, which appends tool results to the current block so the reply that follows can use them.
        const last = chatState.conversation[chatState.conversation.length - 1];
        if (last) {
            last.content.push(msg);
        } else {
            chatState.conversation.push({ role: "user", content: [msg] });
        }
    });
}

/** @deprecated Use commitToolResult */
export const commitCruqToolResult = commitToolResult;

function streamCruqScenario(opts: {
    id: string;
    label: string;
    prompt: string;
    toolId: string;
    intent: string;
    type: CruqEntity;
    operation: CruqOp;
    input: Record<string, unknown>;
    result: ToolResultFixture;
    chunkSize?: number;
}): ChatMessageTestScenario {
    const json = cruqToolJson(opts.intent, opts.type, opts.operation, opts.input);
    return {
        id: opts.id,
        label: opts.label,
        seed: [userPrompt(hashId(opts.id), opts.prompt)],
        phases: [
            {
                kind: "tool_use",
                tool: { name: "cruq_tool", id: opts.toolId },
                chunks: chunkString(json, opts.chunkSize ?? 7),
                result: opts.result,
            },
        ],
    };
}

function completedCruqScenario(opts: {
    id: string;
    label: string;
    prompt: string;
    toolId: string;
    intent: string;
    type: CruqEntity;
    operation: CruqOp;
    input: Record<string, unknown>;
    result: ToolResultFixture;
}): ChatMessageTestScenario {
    const useMsg = toolUseMessage(hashId(opts.id) + 1, opts.toolId, {
        intent: opts.intent,
        type: opts.type,
        operation: opts.operation,
        input: opts.input,
    });
    const resultMsg = toolResultMessage(opts.result.resultMessageId, opts.toolId, "cruq_tool");
    return {
        id: opts.id,
        label: opts.label,
        seed: [
            userPrompt(hashId(opts.id), opts.prompt),
            { role: "assistant", agent_id: AGENT_ID, content: [useMsg] },
            { role: "user", content: [resultMsg] },
        ],
        staticResults: [opts.result],
    };
}

function statusToolJson(intent: string, extra: Record<string, unknown> = {}): string {
    return JSON.stringify({ intent, ...extra });
}

function statusToolUseMessage(
    id: number,
    toolId: string,
    toolName: string,
    input: Record<string, unknown>,
): ChatMessage {
    return {
        id,
        role: "assistant",
        agent_id: AGENT_ID,
        conversation_id: CONV_ID,
        content: [{ type: "tool_use", id: toolId, name: toolName, input }],
    };
}

function statusToolResult(
    toolId: string,
    toolName: string,
    content: unknown,
    opts?: { is_error?: boolean; resultMessageId?: number },
): ToolResultFixture {
    return {
        resultMessageId: opts?.resultMessageId ?? hashId(`result-${toolId}`),
        foldout: {
            type: "tool_result",
            name: toolName,
            tool_id: toolId,
            content,
            ...(opts?.is_error ? { is_error: true } : {}),
        },
    };
}

function streamStatusScenario(opts: {
    id: string;
    label: string;
    prompt: string;
    toolId: string;
    toolName: string;
    intent: string;
    input?: Record<string, unknown>;
    result: ToolResultFixture;
    chunkSize?: number;
}): ChatMessageTestScenario {
    const json = statusToolJson(opts.intent, opts.input ?? {});
    return {
        id: opts.id,
        label: opts.label,
        seed: [userPrompt(hashId(opts.id), opts.prompt)],
        phases: [
            {
                kind: "tool_use",
                tool: { name: opts.toolName, id: opts.toolId },
                chunks: chunkString(json, opts.chunkSize ?? 6),
                result: opts.result,
            },
        ],
    };
}

function completedStatusScenario(opts: {
    id: string;
    label: string;
    prompt: string;
    toolId: string;
    toolName: string;
    intent: string;
    input?: Record<string, unknown>;
    result: ToolResultFixture;
}): ChatMessageTestScenario {
    const useMsg = statusToolUseMessage(hashId(opts.id) + 1, opts.toolId, opts.toolName, {
        intent: opts.intent,
        ...(opts.input ?? {}),
    });
    const resultMsg = toolResultMessage(opts.result.resultMessageId, opts.toolId, opts.toolName);
    return {
        id: opts.id,
        label: opts.label,
        seed: [
            userPrompt(hashId(opts.id), opts.prompt),
            { role: "assistant", agent_id: AGENT_ID, content: [useMsg] },
            { role: "user", content: [resultMsg] },
        ],
        staticResults: [opts.result],
    };
}

const ASSISTANT_MARKDOWN =
    "Here is a **streaming** assistant reply.\n\n- First point\n- Second point\n\nDone.";

const CITED_ARTIFACTS = [
    { type: "artifact" as const, id: 2101, label: "Board meeting notes", created_at: NOW, data: { id: 2101, variable_name: "Board meeting notes", content: "", tag_ids: [] } },
    { type: "artifact" as const, id: 2102, label: "Q3 budget", created_at: NOW, data: { id: 2102, variable_name: "Q3 budget", content: "", tag_ids: [] } },
    { type: "artifact" as const, id: 2103, label: "Launch plan", created_at: NOW, data: { id: 2103, variable_name: "Launch plan", content: "", tag_ids: [] } },
];

const CITED_MARKDOWN = [
    "Here is what the notes say about the launch, with sources.",
    'The board approved the budget: ${A:2101, 3, 4, "The board approved the Q3 budget\\nwith a 5% contingency."} The amount itself is in ${A:2102}.',
    "## Timeline",
    'The launch moved to Friday ${A:2103, 10, 12, "Launch date moved from Wednesday to Friday."} because of the vendor delay ${A:2101, 8, 8, "Vendor shipment is two days late."}',
    "1. Freeze the release branch on Tuesday ${A:2103, 14, 14, \"Code freeze Tuesday 17:00.\"}\n2. Run the final QA pass ${A:2103}",
    "3. Announce on Friday morning ${A:2103, 20, 21, \"Announcement goes out Friday 09:00\\nvia the newsletter.\"}",
    "## Budget",
    'Marketing gets the largest share ${A:2102, 2, 2, "Marketing: 40%"} and engineering follows ${A:2102, 3, 3, "Engineering: 35%"}. The rest is split between support and operations, see ${A:2102, 4, 5}.',
    'Open question: who signs off on the contingency? ${A:2101, 12, 12, "Sign-off owner to be decided next meeting."}',
    "Let me know if you want a summary of any of these sources.",
].join("\n\n");

const LABELED_CITATIONS = [
    { label: "c1", artifact_id: 2101, start_line: 3, end_line: 4, content: "The board approved the Q3 budget\nwith a 5% contingency." },
    { label: "c2", artifact_id: 2103, start_line: 10, end_line: 12, content: "Launch date moved from Wednesday to Friday." },
    { label: "c3", artifact_id: 2101, start_line: 8, end_line: 8, content: "Vendor shipment is two days late." },
    { label: "c4", artifact_id: 2102, start_line: 2, end_line: 2, content: 'Marketing: "40%"' },
    { label: "c5", artifact_id: 2101, start_line: 12, end_line: 12, content: "Sign-off owner to be decided next meeting." },
];

// c4 is written as ${c4}, a mistake models make that both sides still resolve.
const LABELED_MARKDOWN = [
    "Here is what the notes say about the launch, with sources.",
    "The board approved the budget [c1] and the launch moved to Friday [c2] because of the vendor delay [c3].",
    "## Budget",
    "Marketing gets the largest share ${c4}.",
    "Open question: who signs off on the contingency? [c5]",
    "Let me know if you want a summary of any of these sources.",
].join("\n\n");

/** LABELED_MARKDOWN as backend bake_citations saves it. */
const LABELED_MARKDOWN_BAKED = [
    "Here is what the notes say about the launch, with sources.",
    'The board approved the budget ${A:2101, 3, 4, "The board approved the Q3 budget\nwith a 5% contingency."} and the launch moved to Friday ${A:2103, 10, 12, "Launch date moved from Wednesday to Friday."} because of the vendor delay ${A:2101, 8, 8, "Vendor shipment is two days late."}.',
    "## Budget",
    'Marketing gets the largest share ${A:2102, 2, 2, "Marketing: \\"40%\\""}.',
    'Open question: who signs off on the contingency? ${A:2101, 12, 12, "Sign-off owner to be decided next meeting."}',
    "Let me know if you want a summary of any of these sources.",
].join("\n\n");

const CREATE_UPDATE_STREAM: ChatMessageTestScenario[] = [
    streamCruqScenario({
        id: "stream-artifact-create",
        label: "Stream: artifact create → foldout",
        prompt: "Create meeting notes for me.",
        toolId: "toolu_stream_art_create",
        intent: "Creating meeting notes draft",
        type: "artifact",
        operation: "create",
        input: ARTIFACT_CREATE_INPUT,
        result: artifactCreateResult("toolu_stream_art_create", 1101),
    }),
    streamCruqScenario({
        id: "stream-artifact-update",
        label: "Stream: artifact update → foldout",
        prompt: "Update the meeting notes with action items.",
        toolId: "toolu_stream_art_update",
        intent: "Updating meeting notes",
        type: "artifact",
        operation: "update",
        input: ARTIFACT_UPDATE_INPUT,
        result: artifactUpdateResult("toolu_stream_art_update", 101, 2101),
    }),
    streamCruqScenario({
        id: "stream-tag-create",
        label: "Stream: tag create → foldout",
        prompt: "Create a Product tag.",
        toolId: "toolu_stream_tag_create",
        intent: "Creating Product tag draft",
        type: "tag",
        operation: "create",
        input: TAG_CREATE_INPUT,
        result: tagCreateResult("toolu_stream_tag_create", 1201),
    }),
    streamCruqScenario({
        id: "stream-tag-update",
        label: "Stream: tag update → foldout",
        prompt: "Revise the Product tag description.",
        toolId: "toolu_stream_tag_update",
        intent: "Updating Product tag",
        type: "tag",
        operation: "update",
        input: TAG_UPDATE_INPUT,
        result: tagUpdateResult("toolu_stream_tag_update", 201, 2201),
    }),
    streamCruqScenario({
        id: "stream-event-create",
        label: "Stream: event create → foldout",
        prompt: "Schedule a weekly sync.",
        toolId: "toolu_stream_evt_create",
        intent: "Creating weekly sync event",
        type: "event",
        operation: "create",
        input: EVENT_CREATE_INPUT,
        result: eventCreateResult("toolu_stream_evt_create", 1301),
    }),
    streamCruqScenario({
        id: "stream-event-update",
        label: "Stream: event update → foldout",
        prompt: "Move the weekly sync to Room B.",
        toolId: "toolu_stream_evt_update",
        intent: "Updating weekly sync event",
        type: "event",
        operation: "update",
        input: EVENT_UPDATE_INPUT,
        result: eventUpdateResult("toolu_stream_evt_update", 301, 2301),
    }),
];

const CREATE_UPDATE_COMPLETED: ChatMessageTestScenario[] = [
    completedCruqScenario({
        id: "done-artifact-create",
        label: "Done: artifact create foldout",
        prompt: "Create meeting notes for me.",
        toolId: "toolu_done_art_create",
        intent: "Creating meeting notes draft",
        type: "artifact",
        operation: "create",
        input: ARTIFACT_CREATE_INPUT,
        result: artifactCreateResult("toolu_done_art_create", 1111),
    }),
    completedCruqScenario({
        id: "done-artifact-update",
        label: "Done: artifact update foldout",
        prompt: "Update the meeting notes with action items.",
        toolId: "toolu_done_art_update",
        intent: "Updating meeting notes",
        type: "artifact",
        operation: "update",
        input: ARTIFACT_UPDATE_INPUT,
        result: artifactUpdateResult("toolu_done_art_update", 111, 2111),
    }),
    completedCruqScenario({
        id: "done-tag-create",
        label: "Done: tag create foldout",
        prompt: "Create a Product tag.",
        toolId: "toolu_done_tag_create",
        intent: "Creating Product tag draft",
        type: "tag",
        operation: "create",
        input: TAG_CREATE_INPUT,
        result: tagCreateResult("toolu_done_tag_create", 1211),
    }),
    completedCruqScenario({
        id: "done-tag-update",
        label: "Done: tag update foldout",
        prompt: "Revise the Product tag description.",
        toolId: "toolu_done_tag_update",
        intent: "Updating Product tag",
        type: "tag",
        operation: "update",
        input: TAG_UPDATE_INPUT,
        result: tagUpdateResult("toolu_done_tag_update", 211, 2211),
    }),
    completedCruqScenario({
        id: "done-event-create",
        label: "Done: event create foldout",
        prompt: "Schedule a weekly sync.",
        toolId: "toolu_done_evt_create",
        intent: "Creating weekly sync event",
        type: "event",
        operation: "create",
        input: EVENT_CREATE_INPUT,
        result: eventCreateResult("toolu_done_evt_create", 1311),
    }),
    completedCruqScenario({
        id: "done-event-update",
        label: "Done: event update foldout",
        prompt: "Move the weekly sync to Room B.",
        toolId: "toolu_done_evt_update",
        intent: "Updating weekly sync event",
        type: "event",
        operation: "update",
        input: EVENT_UPDATE_INPUT,
        result: eventUpdateResult("toolu_done_evt_update", 311, 2311),
    }),
];

const GALLERY_TOOLS = [
    { toolId: "toolu_gal_art_create", intent: "Creating meeting notes draft", type: "artifact" as const, operation: "create" as const, input: ARTIFACT_CREATE_INPUT, result: artifactCreateResult("toolu_gal_art_create", 1401) },
    { toolId: "toolu_gal_art_update", intent: "Updating meeting notes", type: "artifact" as const, operation: "update" as const, input: ARTIFACT_UPDATE_INPUT, result: artifactUpdateResult("toolu_gal_art_update", 141, 2401) },
    { toolId: "toolu_gal_tag_create", intent: "Creating Product tag draft", type: "tag" as const, operation: "create" as const, input: TAG_CREATE_INPUT, result: tagCreateResult("toolu_gal_tag_create", 1501) },
    { toolId: "toolu_gal_tag_update", intent: "Updating Product tag", type: "tag" as const, operation: "update" as const, input: TAG_UPDATE_INPUT, result: tagUpdateResult("toolu_gal_tag_update", 151, 2501) },
    { toolId: "toolu_gal_evt_create", intent: "Creating weekly sync event", type: "event" as const, operation: "create" as const, input: EVENT_CREATE_INPUT, result: eventCreateResult("toolu_gal_evt_create", 1601) },
    { toolId: "toolu_gal_evt_update", intent: "Updating weekly sync event", type: "event" as const, operation: "update" as const, input: EVENT_UPDATE_INPUT, result: eventUpdateResult("toolu_gal_evt_update", 161, 2601) },
];

const CREATE_UPDATE_GALLERY: ChatMessageTestScenario = {
    id: "gallery-create-update",
    label: "Gallery: all create + update foldouts",
    seed: [
        userPrompt(50, "Exercise every create and update path."),
        {
            role: "assistant",
            agent_id: AGENT_ID,
            content: [
                {
                    id: 51,
                    role: "assistant",
                    agent_id: AGENT_ID,
                    conversation_id: CONV_ID,
                    content: [{ type: "text", text: "Create/update tools with foldouts:" }],
                },
                ...GALLERY_TOOLS.map((t, i) =>
                    toolUseMessage(52 + i, t.toolId, {
                        intent: t.intent,
                        type: t.type,
                        operation: t.operation,
                        input: t.input,
                    }),
                ),
            ],
        },
        {
            role: "user",
            content: GALLERY_TOOLS.map((t) => toolResultMessage(t.result.resultMessageId, t.toolId, "cruq_tool")),
        },
    ],
    staticResults: GALLERY_TOOLS.map((t) => t.result),
};

const STATUS_GALLERY: Array<{
    toolId: string;
    toolName: string;
    intent: string;
    input?: Record<string, unknown>;
    result: ToolResultFixture;
}> = [
    {
        toolId: "toolu_gal_time",
        toolName: "current_time_tool",
        intent: "Reading current time",
        result: statusToolResult("toolu_gal_time", "current_time_tool", { iso: "2026-09-06T12:00:00Z" }),
    },
    {
        toolId: "toolu_gal_cite",
        toolName: "validate_citation",
        intent: "Verifying citation span",
        result: statusToolResult("toolu_gal_cite", "validate_citation", { ok: true }),
    },
    {
        toolId: "toolu_gal_summary",
        toolName: "read_summary_tool",
        intent: "Reading session summary",
        result: statusToolResult("toolu_gal_summary", "read_summary_tool", "Session summary…"),
    },
    {
        toolId: "toolu_gal_chunk",
        toolName: "read_chunk_tool",
        intent: "Reading conversation chunk",
        result: statusToolResult("toolu_gal_chunk", "read_chunk_tool", "Chunk…"),
    },
    {
        toolId: "toolu_gal_evt_sum",
        toolName: "get_event_summary_tool",
        intent: "Reading event summary",
        result: statusToolResult("toolu_gal_evt_sum", "get_event_summary_tool", "Event notes…"),
    },
    {
        toolId: "toolu_gal_consts",
        toolName: "get_conversation_constants",
        intent: "Loading conversation constants",
        result: statusToolResult("toolu_gal_consts", "get_conversation_constants", { locale: "en" }),
    },
    {
        toolId: "toolu_gal_prefs",
        toolName: "modify_user_preferences_tool",
        intent: "Updating preferences",
        result: statusToolResult("toolu_gal_prefs", "modify_user_preferences_tool", { ok: true }),
    },
    {
        toolId: "toolu_gal_agent",
        toolName: "get_agent_tool",
        intent: "Selecting research agent",
        result: statusToolResult("toolu_gal_agent", "get_agent_tool", "Research"),
    },
    {
        toolId: "toolu_gal_sel",
        toolName: "set_selected_objects_tool",
        intent: "Selecting meeting notes",
        result: statusToolResult("toolu_gal_sel", "set_selected_objects_tool", [{ type: "artifact", id: 101 }]),
    },
    {
        toolId: "toolu_gal_fail",
        toolName: "read_chunk_tool",
        intent: "Reading missing chunk",
        result: statusToolResult("toolu_gal_fail", "read_chunk_tool", "Chunk not found", { is_error: true }),
    },
];

export const CHAT_MESSAGE_TEST_SCENARIOS: ChatMessageTestScenario[] = [
    {
        id: "static-text",
        label: "Static user + assistant text",
        seed: [
            userPrompt(1, "Summarise the meeting notes."),
            {
                role: "assistant",
                agent_id: AGENT_ID,
                content: [
                    {
                        id: 2,
                        role: "assistant",
                        agent_id: AGENT_ID,
                        conversation_id: CONV_ID,
                        content: [
                            {
                                type: "text",
                                text: "Sure — here is a short summary of the meeting.\n\n1. Budget approved\n2. Launch date moved to Friday",
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: "stream-text",
        label: "Streaming assistant text",
        seed: [userPrompt(10, "Write a short streaming reply.")],
        phases: [{ kind: "text", chunks: chunkString(ASSISTANT_MARKDOWN, 6) }],
    },
    {
        id: "stream-text-citations",
        label: "Streaming text with many citations",
        seed: [userPrompt(11, "What do the notes say about the launch?")],
        phases: [{ kind: "text", chunks: chunkString(CITED_MARKDOWN, 4) }],
        objects: CITED_ARTIFACTS,
    },
    {
        id: "stream-text-citation-labels",
        label: "Streaming text with validated citation labels",
        seed: [userPrompt(12, "What do the notes say about the launch? Cite them.")],
        phases: [
            {
                kind: "tool_use",
                tool: { name: "validate_citation", id: "toolu_labels_cite" },
                chunks: chunkString(statusToolJson("Validating citations", { citations: LABELED_CITATIONS }), 40),
                result: {
                    ...statusToolResult("toolu_labels_cite", "validate_citation", "Validated 5 citations"),
                    resultFields: { content: "Validated 5 citations", citations_batch: LABELED_CITATIONS },
                },
            },
            { kind: "text", chunks: chunkString(LABELED_MARKDOWN, 4), finalText: LABELED_MARKDOWN_BAKED },
        ],
        objects: CITED_ARTIFACTS,
    },
    {
        id: "text-then-tool",
        label: "Text then artifact create → foldout",
        seed: [userPrompt(40, "Explain briefly, then create notes.")],
        phases: [
            { kind: "text", chunks: chunkString("I'll create a short draft for you.", 5) },
            {
                kind: "tool_use",
                tool: { name: "cruq_tool", id: "toolu_test_cruq_seq" },
                chunks: chunkString(
                    cruqToolJson("Creating meeting notes draft", "artifact", "create", ARTIFACT_CREATE_INPUT),
                    7,
                ),
                result: artifactCreateResult("toolu_test_cruq_seq", 1701),
            },
        ],
    },
    ...CREATE_UPDATE_STREAM,
    ...CREATE_UPDATE_COMPLETED,
    CREATE_UPDATE_GALLERY,
    completedCruqScenario({
        id: "done-artifact-query",
        label: "Done: artifact query (5 items)",
        prompt: "Find recent artifacts on Product tags.",
        toolId: "toolu_done_art_query",
        intent: "Querying recent artifacts",
        type: "artifact",
        operation: "query",
        input: ARTIFACT_QUERY_INPUT,
        result: artifactQueryResult("toolu_done_art_query", ARTIFACT_QUERY_FIVE),
    }),
    streamCruqScenario({
        id: "stream-artifact-query",
        label: "Stream: artifact query → 5 items",
        prompt: "Find recent artifacts on Product tags.",
        toolId: "toolu_stream_art_query",
        intent: "Querying recent artifacts",
        type: "artifact",
        operation: "query",
        input: ARTIFACT_QUERY_INPUT,
        result: artifactQueryResult("toolu_stream_art_query", ARTIFACT_QUERY_FIVE),
    }),

    // ── Status tools (small ToolBox: icon + label - intent) ───────────────
    streamStatusScenario({
        id: "stream-current-time",
        label: "Stream: current_time_tool → status",
        prompt: "What time is it?",
        toolId: "toolu_stream_time",
        toolName: "current_time_tool",
        intent: "Reading current time",
        result: statusToolResult("toolu_stream_time", "current_time_tool", { iso: "2026-09-06T12:00:00Z" }),
    }),
    completedStatusScenario({
        id: "done-current-time",
        label: "Done: current_time_tool status",
        prompt: "What time is it?",
        toolId: "toolu_done_time",
        toolName: "current_time_tool",
        intent: "Reading current time",
        result: statusToolResult("toolu_done_time", "current_time_tool", { iso: "2026-09-06T12:00:00Z" }),
    }),
    completedStatusScenario({
        id: "done-validate-citation",
        label: "Done: validate_citation status",
        prompt: "Verify that citation.",
        toolId: "toolu_done_cite",
        toolName: "validate_citation",
        intent: "Verifying citation span",
        result: statusToolResult("toolu_done_cite", "validate_citation", { ok: true }),
    }),
    completedStatusScenario({
        id: "done-read-summary",
        label: "Done: read_summary_tool status",
        prompt: "Read the session summary.",
        toolId: "toolu_done_summary",
        toolName: "read_summary_tool",
        intent: "Reading session summary",
        result: statusToolResult("toolu_done_summary", "read_summary_tool", "Session summary text…"),
    }),
    completedStatusScenario({
        id: "done-read-chunk",
        label: "Done: read_chunk_tool status",
        prompt: "Read more context.",
        toolId: "toolu_done_chunk",
        toolName: "read_chunk_tool",
        intent: "Reading conversation chunk",
        result: statusToolResult("toolu_done_chunk", "read_chunk_tool", "Chunk content…"),
    }),
    completedStatusScenario({
        id: "done-event-summary",
        label: "Done: get_event_summary_tool status",
        prompt: "Summarise the weekly sync.",
        toolId: "toolu_done_evt_sum",
        toolName: "get_event_summary_tool",
        intent: "Reading event summary",
        result: statusToolResult("toolu_done_evt_sum", "get_event_summary_tool", "Weekly sync notes…"),
    }),
    completedStatusScenario({
        id: "done-conversation-constants",
        label: "Done: get_conversation_constants status",
        prompt: "Load conversation context.",
        toolId: "toolu_done_consts",
        toolName: "get_conversation_constants",
        intent: "Loading conversation constants",
        result: statusToolResult("toolu_done_consts", "get_conversation_constants", { locale: "en" }),
    }),
    completedStatusScenario({
        id: "done-user-preferences",
        label: "Done: modify_user_preferences status",
        prompt: "Prefer shorter replies.",
        toolId: "toolu_done_prefs",
        toolName: "modify_user_preferences_tool",
        intent: "Updating reply length preference",
        result: statusToolResult("toolu_done_prefs", "modify_user_preferences_tool", { ok: true }),
    }),
    completedStatusScenario({
        id: "done-get-agent",
        label: "Done: get_agent_tool status",
        prompt: "Switch to research agent.",
        toolId: "toolu_done_agent",
        toolName: "get_agent_tool",
        intent: "Selecting research agent",
        result: statusToolResult("toolu_done_agent", "get_agent_tool", "Research"),
    }),
    completedStatusScenario({
        id: "done-set-selected",
        label: "Done: set_selected_objects status",
        prompt: "Focus on these notes.",
        toolId: "toolu_done_sel",
        toolName: "set_selected_objects_tool",
        intent: "Selecting meeting notes",
        result: statusToolResult("toolu_done_sel", "set_selected_objects_tool", [{ type: "artifact", id: 101 }]),
    }),
    streamStatusScenario({
        id: "stream-status-fail",
        label: "Stream: status tool → failure",
        prompt: "Try reading a missing chunk.",
        toolId: "toolu_stream_fail",
        toolName: "read_chunk_tool",
        intent: "Reading missing chunk",
        result: statusToolResult("toolu_stream_fail", "read_chunk_tool", "Chunk not found", { is_error: true }),
    }),
    completedStatusScenario({
        id: "done-status-fail",
        label: "Done: status tool failure (x icon)",
        prompt: "Try reading a missing chunk.",
        toolId: "toolu_done_fail",
        toolName: "read_chunk_tool",
        intent: "Reading missing chunk",
        result: statusToolResult("toolu_done_fail", "read_chunk_tool", "Chunk not found", { is_error: true }),
    }),
    completedCruqScenario({
        id: "done-cruq-error",
        label: "Done: cruq_tool error status",
        prompt: "Create notes that fail validation.",
        toolId: "toolu_done_cruq_err",
        intent: "Creating invalid notes",
        type: "artifact",
        operation: "create",
        input: ARTIFACT_CREATE_INPUT,
        result: {
            resultMessageId: hashId("result-toolu_done_cruq_err"),
            entity: "artifact",
            operation: "create",
            foldout: {
                type: "tool_result",
                name: "cruq_tool",
                tool_id: "toolu_done_cruq_err",
                entity_type: "artifact",
                operation: "create",
                is_error: true,
                content: "Validation failed",
            },
        },
    }),
    completedCruqScenario({
        id: "done-artifact-read",
        label: "Done: artifact read (status text)",
        prompt: "Read the meeting notes artifact.",
        toolId: "toolu_done_art_read",
        intent: "Reading meeting notes",
        type: "artifact",
        operation: "read",
        input: { id: 101 },
        result: {
            resultMessageId: hashId("result-toolu_done_art_read"),
            entity: "artifact",
            operation: "read",
            foldout: {
                type: "tool_result",
                name: "cruq_tool",
                tool_id: "toolu_done_art_read",
                entity_type: "artifact",
                operation: "read",
                content: "Artifact body…",
            },
        },
    }),
    {
        id: "gallery-status-tools",
        label: "Gallery: status tools (success + fail)",
        seed: [
            userPrompt(80, "Exercise status-only tool foldouts."),
            {
                role: "assistant",
                agent_id: AGENT_ID,
                content: [
                    {
                        id: 81,
                        role: "assistant",
                        agent_id: AGENT_ID,
                        conversation_id: CONV_ID,
                        content: [{ type: "text", text: "Status tools:" }],
                    },
                    ...STATUS_GALLERY.map((t, i) =>
                        statusToolUseMessage(82 + i, t.toolId, t.toolName, {
                            intent: t.intent,
                            ...(t.input ?? {}),
                        }),
                    ),
                ],
            },
            {
                role: "user",
                content: STATUS_GALLERY.map((t) =>
                    toolResultMessage(t.result.resultMessageId, t.toolId, t.toolName),
                ),
            },
        ],
        staticResults: STATUS_GALLERY.map((t) => t.result),
    },
];

export const DEFAULT_SCENARIO_ID = CHAT_MESSAGE_TEST_SCENARIOS[0].id;

export function getScenario(id: string): ChatMessageTestScenario {
    return CHAT_MESSAGE_TEST_SCENARIOS.find((s) => s.id === id) ?? CHAT_MESSAGE_TEST_SCENARIOS[0];
}

export function cloneSeed(seed: ConversationBlock[]): ConversationBlock[] {
    return structuredClone(seed);
}

export function assemblePhaseContent(phase: StreamPhase): string {
    return phase.chunks.join("");
}

export function finalMessageForPhase(phase: StreamPhase, conversationId = CONV_ID, agentId = AGENT_ID) {
    if (phase.kind === "tool_use" && phase.tool) {
        let input: any = {};
        try {
            input = JSON.parse(assemblePhaseContent(phase));
        } catch {
            input = { raw: assemblePhaseContent(phase) };
        }
        return {
            role: "assistant" as const,
            agent_id: agentId,
            conversation_id: conversationId,
            content: [
                {
                    type: "tool_use" as const,
                    id: phase.tool.id,
                    name: phase.tool.name,
                    input,
                },
            ],
        };
    }
    return {
        role: "assistant" as const,
        agent_id: agentId,
        conversation_id: conversationId,
        content: [{ type: "text" as const, text: phase.finalText ?? assemblePhaseContent(phase) }],
    };
}

/** Wire staticResults mocks for a scenario; tool ids come from foldout.tool_id. */
export function registerScenarioMocks(scenario: ChatMessageTestScenario) {
    clearToolFoldoutMocks();
    const results = scenario.staticResults ?? [];
    seedChatObjects(scenario.objects ?? []);
    if (results.length === 0) return;
    applyCruqResultFixtures(
        results,
        results.map((r) => r.foldout.tool_id as string),
    );
}
